import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Share2 } from 'lucide-react'
import { CONORBIT_ORG_ID } from '@/lib/constants'

function PostsTab() {
  const { data: posts } = useQuery({
    queryKey: ['conorbit-posts'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('posts' as any)
        .select('id, content, status, created_at, platform')
        .eq('org_id', CONORBIT_ORG_ID)
        .order('created_at', { ascending: false })
        .limit(20) as any)
      return (data ?? []) as any[]
    }
  })

  return (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left">
            <th className="px-4 py-3 font-medium text-muted-foreground">Content</th>
            <th className="px-4 py-3 font-medium text-muted-foreground">Platform</th>
            <th className="px-4 py-3 font-medium text-muted-foreground">Status</th>
            <th className="px-4 py-3 font-medium text-muted-foreground">Created</th>
          </tr>
        </thead>
        <tbody>
          {(posts ?? []).map((post: any) => (
            <tr key={post.id} className="border-b last:border-0">
              <td className="px-4 py-3 max-w-xs">
                <p className="truncate text-sm">{post.content}</p>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{post.platform}</td>
              <td className="px-4 py-3">
                <Badge variant="outline" className="text-[10px]">{post.status}</Badge>
              </td>
              <td className="px-4 py-3 text-muted-foreground text-xs">
                {new Date(post.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
          {(posts ?? []).length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">
                No posts for CONORBIT yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function AccountsTab() {
  const { data: accounts } = useQuery({
    queryKey: ['conorbit-social-accounts'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('social_accounts' as any)
        .select('id, platform, handle, status, followers_count')
        .eq('org_id', CONORBIT_ORG_ID) as any)
      return (data ?? []) as any[]
    }
  })

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {(accounts ?? []).map((acc: any) => (
        <Card key={acc.id}>
          <CardContent className="p-4 flex items-center gap-3">
            <Share2 className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{acc.handle}</div>
              <div className="text-xs text-muted-foreground">{acc.platform}</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium">{acc.followers_count?.toLocaleString() ?? ''}</div>
              <Badge variant="outline" className="text-[10px]">{acc.status}</Badge>
            </div>
          </CardContent>
        </Card>
      ))}
      {(accounts ?? []).length === 0 && (
        <p className="text-justify text-sm text-muted-foreground col-span-2 text-center py-8">
          No social accounts linked for CONORBIT.
        </p>
      )}
    </div>
  )
}

function CalendarTab() {
  const { data: scheduled } = useQuery({
    queryKey: ['conorbit-scheduled'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('posts' as any)
        .select('id, content, platform, scheduled_at')
        .eq('org_id', CONORBIT_ORG_ID)
        .eq('status', 'scheduled')
        .order('scheduled_at') as any)
      return (data ?? []) as any[]
    }
  })

  return (
    <div className="space-y-3">
      {(scheduled ?? []).map((post: any) => (
        <div key={post.id} className="flex items-start gap-3 p-3 border rounded-lg">
          <div className="text-xs text-muted-foreground w-24 shrink-0 pt-0.5">
            {new Date(post.scheduled_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{post.content}</p>
            <Badge variant="outline" className="text-[10px] mt-1">{post.platform}</Badge>
          </div>
        </div>
      ))}
      {(scheduled ?? []).length === 0 && (
        <p className="text-justify text-sm text-muted-foreground text-center py-8">No scheduled posts.</p>
      )}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border rounded-lg p-4">
      <div className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  )
}

function AnalyticsTab() {
  const { data: posts } = useQuery({
    queryKey: ['conorbit-posts-analytics'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('posts' as any)
        .select('platform, status, created_at')
        .eq('org_id', CONORBIT_ORG_ID) as any)
      return (data ?? []) as any[]
    }
  })

  const { data: accounts } = useQuery({
    queryKey: ['conorbit-accounts-analytics'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('social_accounts' as any)
        .select('platform, handle, followers_count, status')
        .eq('org_id', CONORBIT_ORG_ID) as any)
      return (data ?? []) as any[]
    }
  })

  const platformCounts: Record<string, number> = {}
  const statusCounts: Record<string, number> = {}
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 30)
  let recentPublished = 0

  for (const p of (posts ?? [])) {
    platformCounts[p.platform] = (platformCounts[p.platform] ?? 0) + 1
    statusCounts[p.status] = (statusCounts[p.status] ?? 0) + 1
    if (p.status === 'published' && new Date(p.created_at) >= cutoff) recentPublished++
  }

  const totalFollowers = (accounts ?? []).reduce(
    (sum: number, a: any) => sum + (a.followers_count ?? 0), 0
  )
  const totalPosts = (posts ?? []).length

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Posts" value={totalPosts} />
        <StatCard label="Published" value={statusCounts['published'] ?? 0} />
        <StatCard label="Last 30 Days" value={recentPublished} />
        <StatCard label="Total Followers" value={totalFollowers.toLocaleString()} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium mb-3">Posts by Platform</h3>
          {Object.keys(platformCounts).length === 0 ? (
            <p className="text-justify text-sm text-muted-foreground">No posts yet.</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(platformCounts).sort((a, b) => b[1] - a[1]).map(([platform, count]) => {
                const pct = totalPosts > 0 ? Math.round((count / totalPosts) * 100) : 0
                return (
                  <div key={platform} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="capitalize">{platform}</span>
                      <span className="text-muted-foreground">{count} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-[#0D2240] rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-medium mb-3">Posts by Status</h3>
          <div className="space-y-2">
            {Object.entries(statusCounts).sort((a, b) => b[1] - a[1]).map(([status, count]) => {
              const pct = totalPosts > 0 ? Math.round((count / totalPosts) * 100) : 0
              return (
                <div key={status} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="capitalize">{status}</span>
                    <span className="text-muted-foreground">{count} ({pct}%)</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-[#006B3C] rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {(accounts ?? []).length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">Account Performance</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Platform</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Handle</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Followers</th>
                  <th className="px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {[...(accounts ?? [])].sort((a: any, b: any) => (b.followers_count ?? 0) - (a.followers_count ?? 0)).map((acc: any, i: number) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="px-4 py-2.5 capitalize text-xs">{acc.platform}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{acc.handle}</td>
                    <td className="px-4 py-2.5 text-xs font-medium">{(acc.followers_count ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        acc.status === 'active' ? 'bg-[#E1F5EE] text-[#0F6E56]' : 'bg-muted text-muted-foreground'
                      }`}>{acc.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export function ConorbitSocialView() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">CONORBIT Social</h1>
        <p className="text-justify text-muted-foreground text-sm mt-1">
          Social media content and accounts for the CONORBIT platform.
        </p>
      </div>

      <Tabs defaultValue="posts">
        <TabsList>
          <TabsTrigger value="posts">Posts</TabsTrigger>
          <TabsTrigger value="accounts">Accounts</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>
        <TabsContent value="posts" className="mt-4"><PostsTab /></TabsContent>
        <TabsContent value="accounts" className="mt-4"><AccountsTab /></TabsContent>
        <TabsContent value="calendar" className="mt-4"><CalendarTab /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
      </Tabs>
    </div>
  )
}
